import { MASTER_ALERT_KEY } from "../constants";
import {
  type AlertChannel,
  type AlertChannelEdits,
  type HomeHealthAlertEnabledMap,
} from "../types";
import { isNil } from "@clipboard-health/util-ts";

type AlertSettings = Record<string, Record<string, unknown>>;
type AlertEntry = Record<string, unknown>;

/**
 * Builds the alertSettings blob to submit when saving Home Health preferences.
 *
 * Round-trip invariant: every key in `existing` must appear in the result —
 * only the channel/alertKey pairs named in `edits` are mutated. This
 * preserves keys the current view doesn't surface (e.g. an LTC workplace's
 * alerts on a multi-workplace user) instead of silently dropping them.
 *
 * The "All" key writes directly to `<channel>.All`, which the backend
 * mirrors to Knock's channel-level mute (suppresses every workflow on that
 * channel for the user across every workplace).
 */
export function buildMergedAlertSettings(params: {
  existing: AlertSettings | undefined;
  edits: AlertChannelEdits;
}): AlertSettings {
  const { existing, edits } = params;

  const result: AlertSettings = { ...existing };

  for (const [channel, channelEdits] of Object.entries(edits) as Array<
    [AlertChannel, HomeHealthAlertEnabledMap | undefined]
  >) {
    if (!channelEdits) {
      continue;
    }

    const existingChannel = (existing?.[channel] ?? {}) as Record<string, AlertEntry | undefined>;
    const updatedChannel: Record<string, AlertEntry | undefined> = { ...existingChannel };

    for (const [alertKey, enabled] of Object.entries(channelEdits)) {
      updatedChannel[alertKey] = { ...existingChannel[alertKey], enabled };
    }

    result[channel] = updatedChannel as Record<string, unknown>;
  }

  return result;
}

/**
 * The backend's Knock sync (buildUpdateQueryFromAlertSettings, backend-main)
 * iterates its canonical alert→workflow map and writes an explicit `false`
 * for any alert key absent from the user's document (ALCH-5851), so for
 * workflow rows a missing entry means the notification is OFF.
 *
 * The "All" key is the one exception: the sync only writes the channel-level
 * mute when the All entry exists, so a missing "All" means the channel is not
 * muted.
 */
export function getChannelRowEnabled(
  alertSettings: AlertSettings | undefined,
  channel: AlertChannel,
  alertKey: string
): boolean {
  const entry = alertSettings?.[channel]?.[alertKey] as AlertEntry | undefined;
  if (isNil(entry?.enabled)) {
    return alertKey === MASTER_ALERT_KEY;
  }
  return Boolean(entry.enabled);
}
